import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, User, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { setUserPersonProperties, computeInternalFlags } from "@/lib/posthog";

type ActivateStep = 'loading' | 'error' | 'claimed-prompt' | 'selection' | 'auth-choice';

const Activate = () => {
  const navigate = useNavigate();
  const { id: memorialId } = useParams<{ id: string }>();
  // Don't use signOut from useAuth as it forces a redirect
  const { isAuthenticated, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState<ActivateStep>('loading');
  const [selectedOption, setSelectedOption] = useState<"self" | "loved_one" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [memorialName, setMemorialName] = useState<string>("");
  const [isInitializing, setIsInitializing] = useState(true);

  // Validate memorial first, then decide whether to sign out / redirect.
  // We can't sign out unconditionally up front anymore: if the visitor is the
  // memorial's owner returning via the activation email (a common re-entry
  // point), we want to bounce them straight to their admin instead of
  // signing them out and showing an error.
  useEffect(() => {
    const initializeActivation = async () => {
      try {
        if (!memorialId) {
          setErrorMessage("Invalid activation link. No memorial ID provided.");
          setCurrentStep('error');
          setIsInitializing(false);
          return;
        }

        // Capture current session before we decide whether to clear it
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        // Check if memorial exists using public view (accessible to anonymous users)
        const { data: memorial, error: memorialError } = await supabase
          .from('memorials_public')
          .select('id, is_claimed, first_name, last_name')
          .eq('id', memorialId)
          .single();

        if (memorialError || !memorial) {
          setErrorMessage("This memorial was not found. Please check your activation link.");
          setCurrentStep('error');
          setIsInitializing(false);
          return;
        }

        setMemorialName(buildMemorialDisplayName(memorial));

        if (memorial.is_claimed) {
          if (existingSession) {
            // Probe whether the signed-in user owns this memorial. RLS on
            // memorials is permissive enough that the row can come back even
            // when user_id doesn't match — so we filter on user_id
            // explicitly rather than relying on RLS to do it for us.
            const { data: ownedMemorial } = await supabase
              .from('memorials')
              .select('id')
              .eq('id', memorialId)
              .eq('user_id', existingSession.user.id)
              .maybeSingle();

            if (ownedMemorial) {
              // Owner returning via the email — take them straight to admin
              navigate(`/admin?memorialId=${memorialId}`, { replace: true });
              return;
            }

            // Signed in as someone who doesn't own this memorial.
            // Sign them out so the "Sign In" button can route the real owner in.
            await supabase.auth.signOut();
            setErrorMessage("This memorial has already been claimed by another account.");
            setCurrentStep('error');
            setIsInitializing(false);
            return;
          }

          // Not signed in but memorial is claimed — soft prompt to sign in
          setCurrentStep('claimed-prompt');
          setIsInitializing(false);
          return;
        }

        // Memorial is unclaimed — original activation flow.
        // Sign out any lingering session so the next claim is unambiguous.
        if (existingSession) {
          await supabase.auth.signOut();
        }

        // Attribute the pre-signup visit to the family if the activation email passed ?email=.
        // setPersonProperties (not identify) so we don't rebind the anonymous distinct_id —
        // signUp() will posthog.alias() that anonymous id into the real user.id, which is
        // the only way pre- and post-signup events end up under one PostHog person.
        const emailParam = new URLSearchParams(window.location.search).get('email');
        if (emailParam) {
          setUserPersonProperties({ email: emailParam, pre_signup: true, memorial_id: memorialId, ...computeInternalFlags(emailParam) });
          // Scrub email from the URL so it doesn't leak via shared screens, bookmarks, or copied links
          window.history.replaceState({}, '', `/activate/${memorialId}`);
        }

        setCurrentStep('selection');
      } catch (error) {
        console.error('[Activate] Error validating memorial:', error);
        setErrorMessage("An error occurred while validating your activation link.");
        setCurrentStep('error');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeActivation();
  }, [memorialId, navigate]);

  // Note: No redirect useEffect needed here - the Auth page handles the redirect
  // after successful authentication via the b2b_claim parameter

  const handleContinue = () => {
    if (!selectedOption) return;
    setCurrentStep('auth-choice');
  };

  const handleAuthChoice = (type: 'login' | 'signup') => {
    // Navigate to auth page with b2b_claim flag and memorial purpose
    const purposeParam = selectedOption ? `&memorialPurpose=${selectedOption}` : '';
    const authType = type === 'login' ? 'login=true' : 'signup=true';
    
    // Use b2b_claim=true to tell Auth page this is a B2B activation (no popup)
    navigate(`/auth?b2b_claim=${memorialId}&${authType}${purposeParam}`);
  };

  const handleBack = () => {
    setCurrentStep('selection');
  };

  const getEmpathyMessage = () => {
    if (selectedOption === "self") {
      return "You're giving a meaningful gift to those who'll carry your story forward. We'll help you capture it beautifully.";
    }
    if (selectedOption === "loved_one") {
      return "This is a beautiful way to remember them. We'll make it simple to bring their story to life.";
    }
    return null;
  };

  // Loading state
  if (currentStep === 'loading') {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-2xl space-y-3 sm:space-y-5">
          <div className="text-center mb-3 sm:mb-6">
            <img 
              src="/lovable-uploads/pontis-logo.svg" 
              alt="Pontis" 
              className="h-10 sm:h-16 mx-auto mb-2 sm:mb-4"
            />
          </div>
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg">
            <CardContent className="p-8 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary mb-4" />
              <p className="text-brand-secondary">Validating your activation link...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Already-claimed soft prompt: shown when memorial is claimed AND visitor
  // is not signed in. The harsh "Unable to Activate" error is reserved for
  // the case where someone signed in *as the wrong account* hits this URL.
  if (currentStep === 'claimed-prompt') {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-2xl space-y-3 sm:space-y-5">
          <div className="text-center mb-3 sm:mb-6">
            <img
              src="/lovable-uploads/pontis-logo.svg"
              alt="Pontis"
              className="h-10 sm:h-16 mx-auto mb-2 sm:mb-4"
            />
          </div>
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-4 rounded-full bg-brand-primary/10">
                  <Heart className="w-8 h-8 text-brand-primary" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-brand-text-primary">
                  Welcome back
                </h2>
                <p className="text-brand-secondary">
                  {memorialName
                    ? `${memorialName}'s memorial is already set up.`
                    : 'This memorial is already set up.'}{' '}
                  Sign in to view it.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button
                    onClick={() => navigate(`/auth?b2b_claim=${memorialId}&login=true`)}
                    className="bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-brand-primary/30"
                  >
                    Go Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <p className="text-center text-xs sm:text-sm text-brand-secondary">
            Need help?{' '}
            <a href="mailto:support@pontis.life" className="text-brand-accent hover:underline">
              support@pontis.life
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (currentStep === 'error') {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-2xl space-y-3 sm:space-y-5">
          <div className="text-center mb-3 sm:mb-6">
            <img 
              src="/lovable-uploads/pontis-logo.svg" 
              alt="Pontis" 
              className="h-10 sm:h-16 mx-auto mb-2 sm:mb-4"
            />
          </div>
          <Card className="border-2 border-red-200 bg-white shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="p-4 rounded-full bg-red-100">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-brand-text-primary">
                  Unable to Activate
                </h2>
                <p className="text-brand-secondary">
                  {errorMessage}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button
                    onClick={() => navigate('/auth')}
                    className="bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="border-brand-primary/30"
                  >
                    Go Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <p className="text-center text-xs sm:text-sm text-brand-secondary">
            Need help?{" "}
            <a href="mailto:support@pontis.life" className="text-brand-accent hover:underline">
              support@pontis.life
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-background flex items-center justify-center p-3 sm:p-6">
      <div className="w-full max-w-2xl space-y-3 sm:space-y-5">
        {/* Logo */}
        <div className="text-center mb-3 sm:mb-6">
          <img 
            src="/lovable-uploads/pontis-logo.svg" 
            alt="Pontis" 
            className="h-10 sm:h-16 mx-auto mb-2 sm:mb-4"
          />
          <h1 className="font-serif text-xl sm:text-3xl font-bold text-brand-text-primary">We're glad you're here.</h1>
          <p className="text-brand-secondary text-xs sm:text-base mt-1 sm:mt-2">Let's create a space where their story — and their impact — will live on.</p>
        </div>

        {/* Step 1: Selection */}
        {currentStep === 'selection' && (
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg animate-in fade-in duration-300">
            <CardContent className="p-4 sm:p-8">
              <h2 className="font-serif text-lg sm:text-2xl font-semibold text-center text-brand-text-primary mb-4 sm:mb-8">
                Who will this memorial honor?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-6">
                {/* Self Option */}
                <button
                  onClick={() => setSelectedOption("self")}
                  className={`p-3 sm:p-6 rounded-lg border-2 transition-all ${
                    selectedOption === "self"
                      ? "border-brand-primary bg-brand-primary/10 shadow-lg"
                      : "border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div className={`p-2 sm:p-4 rounded-full transition-colors ${
                      selectedOption === "self" ? "bg-brand-primary text-brand-text-light" : "bg-brand-secondary/20 text-brand-text-primary"
                    }`}>
                      <User className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <h3 className="font-serif text-base sm:text-lg font-semibold text-brand-text-primary">Myself</h3>
                    <p className="text-xs sm:text-sm text-brand-secondary text-center">
                      I'm preserving my story for those I love.
                    </p>
                  </div>
                </button>

                {/* Loved One Option */}
                <button
                  onClick={() => setSelectedOption("loved_one")}
                  className={`p-3 sm:p-6 rounded-lg border-2 transition-all ${
                    selectedOption === "loved_one"
                      ? "border-brand-primary bg-brand-primary/10 shadow-lg"
                      : "border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2 sm:gap-3">
                    <div className={`p-2 sm:p-4 rounded-full transition-colors ${
                      selectedOption === "loved_one" ? "bg-brand-primary text-brand-text-light" : "bg-brand-secondary/20 text-brand-text-primary"
                    }`}>
                      <Heart className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    <h3 className="font-serif text-base sm:text-lg font-semibold text-brand-text-primary">A Loved One</h3>
                    <p className="text-xs sm:text-sm text-brand-secondary text-center">
                      I'm creating a tribute to someone who shaped my life.
                    </p>
                  </div>
                </button>
              </div>

              {/* Continue Button */}
              <Button
                onClick={handleContinue}
                disabled={!selectedOption}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                size="lg"
              >
                Begin Creating
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Auth Choice */}
        {currentStep === 'auth-choice' && (
          <Card className="border-2 border-brand-primary/30 bg-white shadow-lg animate-in fade-in duration-300">
            <CardContent className="p-4 sm:p-8">
              {/* Empathy Message */}
              <div className="p-4 sm:p-6 bg-brand-accent/10 rounded-lg mb-6 sm:mb-8">
                <p className="text-center text-sm sm:text-base text-brand-text-primary italic">
                  {getEmpathyMessage()}
                </p>
              </div>

              <h2 className="font-serif text-lg sm:text-2xl font-semibold text-center text-brand-text-primary mb-6 sm:mb-8">
                Let's get you started
              </h2>

              <div className="space-y-3 sm:space-y-4">
                {/* Sign Up Button */}
                <Button
                  onClick={() => handleAuthChoice('signup')}
                  className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-text-light"
                  size="lg"
                >
                  Create an Account
                </Button>

                {/* Log In Button */}
                <Button
                  onClick={() => handleAuthChoice('login')}
                  variant="outline"
                  className="w-full border-2 border-brand-primary/30 hover:border-brand-accent hover:bg-brand-primary/5 text-brand-text-primary"
                  size="lg"
                >
                  I Already Have an Account
                </Button>

                {/* Back Link */}
                <button
                  onClick={handleBack}
                  className="w-full text-sm text-brand-secondary hover:text-brand-accent transition-colors pt-2"
                >
                  ← Go back
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Support Text */}
        <p className="text-center text-xs sm:text-sm text-brand-secondary">
          We're always here if you need help —{" "}
          <a href="mailto:support@pontis.life" className="text-brand-accent hover:underline">
            support@pontis.life
          </a>
        </p>
      </div>
    </div>
  );
};

export default Activate;
